import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import StandardLayout from '../../../components/layout/StandardLayout';
import { loadCategories } from '../../../services/questionAPIService';
import { XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';

const QuestionFormBase = ({ 
    initialData = {
        question_text: '',
        answer_text: '',
        explanation_text: '',
        category_id: '',
        question_image: null,
        answer_image: null,
        explanation_image: null
    },
    onSubmit,
    submitButtonText,
    title,
    isEdit = false
}) => {
    const [formData, setFormData] = useState({
        question_text: initialData.question_text || '',
        answer_text: initialData.answer_text || '',
        explanation_text: initialData.explanation_text || '',
        category_id: initialData.category_id || '',
    });
    
    const [imageFiles, setImageFiles] = useState({
        question_image: null,
        answer_image: null,
        explanation_image: null
    });
    
    const [imagePreviews, setImagePreviews] = useState({
        question_image: isEdit ? initialData.question_image : null,
        answer_image: isEdit ? initialData.answer_image : null,
        explanation_image: isEdit ? initialData.explanation_image : null
    });
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const [imagesToDelete, setImagesToDelete] = useState({
        delete_question_image: false,
        delete_answer_image: false,
        delete_explanation_image: false
    });

    useEffect(() => {
        loadCategoryData();
    }, []);

    const loadCategoryData = async () => {
        try {
            const data = await loadCategories();
            setCategories(data);
            setError(null);
        } catch (err) {
            setError('Fehler beim Laden der Kategorien');
            console.error('Error loading categories:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageUpload = (e, fieldName) => {
        const file = e.target.files[0];
        if (file) {
            // Store the actual file
            setImageFiles(prev => ({
                ...prev,
                [fieldName]: file
            }));

            // Create preview URL
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreviews(prev => ({
                    ...prev,
                    [fieldName]: reader.result
                }));
            };
            reader.readAsDataURL(file);

            // If we're in edit mode and there was an initial image, set the delete flag
            // because the old image should be deleted when a new one is uploaded
            if (isEdit && initialData[fieldName]) {
                setImagesToDelete(prev => ({
                    ...prev,
                    [`delete_${fieldName}`]: true
                }));
            }
        }
    };

    const handleImageDelete = (fieldName) => {
        // Reset the file input
        const fileInput = document.querySelector(`input[name="${fieldName}"]`);
        if (fileInput) {
            fileInput.value = '';
        }
        
        // Clear both the file and preview
        setImageFiles(prev => ({
            ...prev,
            [fieldName]: null
        }));
        setImagePreviews(prev => ({
            ...prev,
            [fieldName]: null
        }));

        // Set delete flag if we're in edit mode and there was an initial image
        if (isEdit && initialData[fieldName]) {
            setImagesToDelete(prev => ({
                ...prev,
                [`delete_${fieldName}`]: true
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const submissionData = {
                ...formData,
                image_update: imagesToDelete
            };
            
            // Handle images differently for edit mode
            if (isEdit) {
                // Only include images that have been changed (new File objects)
                if (imageFiles.question_image) {
                    submissionData.question_image = imageFiles.question_image;
                } else if (imagePreviews.question_image && !imagesToDelete.delete_question_image) {
                    submissionData.question_image = imagePreviews.question_image;
                }
                
                if (imageFiles.answer_image) {
                    submissionData.answer_image = imageFiles.answer_image;
                } else if (imagePreviews.answer_image && !imagesToDelete.delete_answer_image) {
                    submissionData.answer_image = imagePreviews.answer_image;
                }
                
                if (imageFiles.explanation_image) {
                    submissionData.explanation_image = imageFiles.explanation_image;
                } else if (imagePreviews.explanation_image && !imagesToDelete.delete_explanation_image) {
                    submissionData.explanation_image = imagePreviews.explanation_image;
                }
            } else {
                // For create mode, just include the new files
                submissionData.question_image = imageFiles.question_image;
                submissionData.answer_image = imageFiles.answer_image;
                submissionData.explanation_image = imageFiles.explanation_image;
            }
            
            await onSubmit(submissionData);
            navigate('/admin/questions');
        } catch (error) {
            console.error('Error submitting form:', error);
            setError('Fehler beim Speichern der Frage');
        }
    };

    const handleCancel = () => {
        navigate('/admin/questions');
    };

    if (loading) {
        return (
            <StandardLayout>
                <div className="flex justify-center items-center min-h-screen">
                    <span className="loading loading-spinner loading-lg text-primary"></span>
                </div>
            </StandardLayout>
        );
    }

    if (error) {
        return (
            <StandardLayout>
                <div className="container mx-auto px-4 py-8">
                    <div className="alert alert-error">
                        <XMarkIcon className="w-6 h-6" />
                        <span>{error}</span>
                    </div>
                </div>
            </StandardLayout>
        );
    }

    return (
        <StandardLayout>
            <div className="container mx-auto px-4 py-8">
                <div className="max-w-2xl mx-auto">
                    <h1 className="text-2xl font-bold mb-6">{title}</h1>
                    
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Kategorie</span>
                            </label>
                            <select
                                name="category_id"
                                value={formData.category_id}
                                onChange={handleInputChange}
                                className="select select-bordered w-full"
                                required
                            >
                                <option value="">Kategorie auswählen</option>
                                {categories.map(category => (
                                    <option key={category.id} value={category.id}>
                                        {category.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Frage</span>
                            </label>
                            <textarea
                                name="question_text"
                                value={formData.question_text}
                                onChange={handleInputChange}
                                className="textarea textarea-bordered h-24"
                                placeholder="Geben Sie die Frage ein"
                                required
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Frage Bild</span>
                            </label>
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <input
                                        type="file"
                                        name="question_image"
                                        onChange={(e) => handleImageUpload(e, 'question_image')}
                                        className="file-input file-input-bordered w-full"
                                        accept="image/*"
                                    />
                                    {isEdit && initialData.question_image && !imageFiles.question_image && (
                                        <span className="text-sm text-gray-500 truncate max-w-xs">
                                            Aktuelles Bild: {initialData.question_image.split('/').pop()}
                                        </span>
                                    )}
                                </div>
                                {imagePreviews.question_image && (
                                    <div className="relative">
                                        <img 
                                            src={imagePreviews.question_image} 
                                            alt="Frage" 
                                            className="max-h-40 object-contain"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleImageDelete('question_image')}
                                            className="btn btn-circle btn-sm btn-error absolute top-2 right-2"
                                            title="Bild löschen"
                                        >
                                            <TrashIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Antwort</span>
                            </label>
                            <textarea
                                name="answer_text"
                                value={formData.answer_text}
                                onChange={handleInputChange}
                                className="textarea textarea-bordered h-24"
                                placeholder="Geben Sie die Antwort ein"
                                required
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Antwort Bild</span>
                            </label>
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <input
                                        type="file"
                                        name="answer_image"
                                        onChange={(e) => handleImageUpload(e, 'answer_image')}
                                        className="file-input file-input-bordered w-full"
                                        accept="image/*"
                                    />
                                    {isEdit && initialData.answer_image && !imageFiles.answer_image && (
                                        <span className="text-sm text-gray-500 truncate max-w-xs">
                                            Aktuelles Bild: {initialData.answer_image.split('/').pop()}
                                        </span>
                                    )}
                                </div>
                                {imagePreviews.answer_image && (
                                    <div className="relative">
                                        <img 
                                            src={imagePreviews.answer_image} 
                                            alt="Antwort" 
                                            className="max-h-40 object-contain"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleImageDelete('answer_image')}
                                            className="btn btn-circle btn-sm btn-error absolute top-2 right-2"
                                            title="Bild löschen"
                                        >
                                            <TrashIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Erklärung</span>
                            </label>
                            <textarea
                                name="explanation_text"
                                value={formData.explanation_text}
                                onChange={handleInputChange}
                                className="textarea textarea-bordered h-24"
                                placeholder="Geben Sie eine Erklärung ein"
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Erklärungs Bild</span>
                            </label>
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <input
                                        type="file"
                                        name="explanation_image"
                                        onChange={(e) => handleImageUpload(e, 'explanation_image')}
                                        className="file-input file-input-bordered w-full"
                                        accept="image/*"
                                    />
                                    {isEdit && initialData.explanation_image && !imageFiles.explanation_image && (
                                        <span className="text-sm text-gray-500 truncate max-w-xs">
                                            Aktuelles Bild: {initialData.explanation_image.split('/').pop()}
                                        </span>
                                    )}
                                </div>
                                {imagePreviews.explanation_image && (
                                    <div className="relative">
                                        <img 
                                            src={imagePreviews.explanation_image} 
                                            alt="Erklärung" 
                                            className="max-h-40 object-contain"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleImageDelete('explanation_image')}
                                            className="btn btn-circle btn-sm btn-error absolute top-2 right-2"
                                            title="Bild löschen"
                                        >
                                            <TrashIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex justify-end gap-4">
                            <button
                                type="button"
                                onClick={handleCancel}
                                className="btn btn-ghost"
                            >
                                Abbrechen
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                            >
                                {submitButtonText}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </StandardLayout>
    );
};

export default QuestionFormBase;